<script context="module" lang="ts">
    import { browser } from '$app/environment';

    // CLIENT RENDER LOGIC
    const clientCssRefs = new Set<string>();
    const clientScriptRefs = new Set<string>();
    const loadingScriptRefs = new Map<string, Promise<void>>();

    const MAX_RETRIES = 3;
    const RETRY_DELAY = 500;

    const handleStyle = (style: string) => {
        if (!style || clientCssRefs.has(style)) return;
        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.href = style;
        preload.as = 'style';
        document.head.appendChild(preload);

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = style;
        link.type = 'text/css';
        document.head.appendChild(link);
        clientCssRefs.add(style);
    };

    const loadScriptWithRetry = async (
        src: string,
        type?: string,
        retries = MAX_RETRIES,
        delay = RETRY_DELAY
    ): Promise<void> =>
        new Promise((resolve, reject) => {
            const attempt = (remainingRetries: number) => {
                const script = document.createElement('script');

                if (type) {
                    script.type = type;
                }

                script.src = `${src}?cb=${Date.now()}`;
                script.defer = true;

                script.onload = () => resolve();
                script.onerror = () => {
                    script.remove();
                    if (remainingRetries > 0) {
                        setTimeout(() => attempt(remainingRetries - 1), delay);
                    } else {
                        reject(new Error(`Failed to load script: ${src}`));
                    }
                };

                document.head.appendChild(script);
            };

            attempt(retries);
        });

    const handleScript = async (src: string) => {
        if (!src || clientScriptRefs.has(src)) return;

        if (loadingScriptRefs.has(src)) {
            return loadingScriptRefs.get(src);
        }

        const type =
            src.includes('bootstrap') || src.includes('registration-poc') ? 'module' : undefined;

        const loadPromise = loadScriptWithRetry(src, type)
            .then(() => {
                clientScriptRefs.add(src);
                loadingScriptRefs.delete(src);
            })
            .catch((error) => {
                loadingScriptRefs.delete(src);
                console.error(error);
            });

        loadingScriptRefs.set(src, loadPromise);

        return loadPromise;
    };
    const handleClientReferences = (cssReferences: string[], scriptReferences: string[]) => {
        if (!browser) return;
        cssReferences.forEach(handleStyle);
        scriptReferences.forEach(handleScript);
    };

    // COPY SSR REFERENCES BEFORE HYDRATION REMOVES THEM
    if (browser) {
        Array.from(document.querySelectorAll('[data-loaded-ssr]')).forEach((node) => {
            const href = node.getAttribute('href');
            if (href) handleStyle(href);
        });
    }
</script>

<script lang="ts">
    export let styles: string[] = [];
    export let scripts: string[] = [];

    $: if (browser) handleClientReferences(styles, scripts);
</script>

<svelte:head>
    {#if !browser}
        <!-- 
            Anything here will be removed once the component is hydrated on the
            browser side. However, before the hydration takes place, we take 
            over and just copy / duplicate the entry via takeOverReferences().
            This should make it so styles do not flash on the screen.
            These entries will probably be duplicate as the server side renderer
            does not have a runtime to be aware of what was loaded in DOM 
            already. This is fine since we copy only once in client.
        -->
        {#each styles as href}
            {#if href}
                <link rel="preload" {href} as="style" />
                <link rel="stylesheet" data-loaded-ssr type="text/css" {href} />
            {/if}
        {/each}
    {/if}
</svelte:head>
